/**
 * @file     useBackToClose.ts
 * @purpose  Phone Back closes the top public overlay instead of leaving the
 *           page. Each open overlay owns one history entry on a module-level
 *           stack; Back pops the top one, and an overlay closed any other way
 *           (X, backdrop, Escape) gives its entry back with history.back()
 *           only while it is still on top and no navigation is under way.
 */
'use client';

import { useEffect, useRef } from 'react';
import useMediaQuery from '@mui/material/useMediaQuery';

export const BACK_TO_CLOSE_PHONE_QUERY = '(max-width: 599.95px)';

const STATE_KEY = '__rltaOverlay';
// A link click this recent means the close that follows is a navigation, and
// a history.back() would cancel it (Next's traverse discards a pending push).
const NAV_CLICK_WINDOW_MS = 1000;

type EntryStatus = 'queued' | 'live' | 'dead';

type Entry = {
  token: string;
  status: EntryStatus;
  url: string | undefined;
  pathname: string;
  onBack: () => void;
};

const stack: Entry[] = [];
let pendingBacks = 0;
let lastNavClickAt = Number.NEGATIVE_INFINITY;
let listening = false;
let seq = 0;

function pushEntry(entry: Entry) {
  // No __NA here: Next's patched pushState copies its own keys over and only
  // syncs useSearchParams for states it did not write itself.
  window.history.pushState({ [STATE_KEY]: entry.token }, '', entry.url);
  entry.status = 'live';
  entry.pathname = window.location.pathname;
}

function goBack(steps: number) {
  pendingBacks += 1;
  window.history.go(-steps);
}

function flushQueued() {
  for (const entry of stack) if (entry.status === 'queued') pushEntry(entry);
}

function trailingDead(): number {
  let n = 0;
  for (let i = stack.length - 1; i >= 0 && stack[i].status === 'dead'; i -= 1) n += 1;
  return n;
}

function navigating(entry: Entry): boolean {
  return Date.now() - lastNavClickAt < NAV_CLICK_WINDOW_MS || window.location.pathname !== entry.pathname;
}

function settle() {
  const n = trailingDead();
  if (n === 0) return;
  const top = stack[stack.length - 1];
  stack.splice(stack.length - n, n);
  if (navigating(top)) return;
  goBack(n);
}

function onPopState(event: PopStateEvent) {
  if (pendingBacks > 0) {
    pendingBacks -= 1;
    if (pendingBacks === 0) flushQueued();
    return;
  }
  if (stack.length === 0) return;
  const token: unknown = (event.state as Record<string, unknown> | null)?.[STATE_KEY];
  const at = typeof token === 'string' ? stack.findIndex((entry) => entry.token === token) : -1;
  // Forward into an entry this page no longer tracks: nothing to close.
  if (typeof token === 'string' && at === -1) return;
  const popped = at >= 0 ? stack.splice(at + 1) : stack.splice(-1);
  for (const entry of popped.reverse()) {
    if (entry.status !== 'live') continue;
    entry.status = 'dead';
    entry.onBack();
  }
  const n = trailingDead();
  if (n > 0) {
    stack.splice(stack.length - n, n);
    goBack(n);
  }
}

function onClickCapture(event: MouseEvent) {
  if (stack.length === 0) return;
  if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
  const anchor = (event.target as Element | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
  if (!anchor || (anchor.target && anchor.target !== '_self') || anchor.hasAttribute('download')) return;
  const next = new URL(anchor.href, window.location.href);
  const sameDocument =
    next.origin === window.location.origin &&
    next.pathname === window.location.pathname &&
    next.search === window.location.search;
  if (!sameDocument) lastNavClickAt = Date.now();
}

function listen() {
  if (listening) return;
  listening = true;
  window.addEventListener('popstate', onPopState);
  document.addEventListener('click', onClickCapture, true);
}

function openEntry(onBack: () => void, url: string | undefined): Entry {
  listen();
  seq += 1;
  const entry: Entry = { token: `o${seq}`, status: 'queued', url, pathname: '', onBack };
  stack.push(entry);
  if (pendingBacks === 0) pushEntry(entry);
  return entry;
}

function release(entry: Entry) {
  const index = stack.indexOf(entry);
  if (index === -1) return;
  if (entry.status === 'queued') {
    stack.splice(index, 1);
    return;
  }
  entry.status = 'dead';
  // Deferred so an overlay opened by the same click (a menu item that opens a
  // sheet) lands first; the menu's entry then waits under the sheet's.
  window.setTimeout(settle, 0);
}

function retarget(entry: Entry, url: string | undefined) {
  entry.url = url;
  if (entry.status !== 'live' || stack[stack.length - 1] !== entry) return;
  const state = window.history.state as Record<string, unknown> | null;
  if (state?.[STATE_KEY] !== entry.token) return;
  window.history.replaceState({ [STATE_KEY]: entry.token }, '', url);
}

export function __resetBackToCloseForTests() {
  stack.length = 0;
  pendingBacks = 0;
  lastNavClickAt = Number.NEGATIVE_INFINITY;
}

export type BackToCloseOptions = {
  /** URL for the overlay's history entry (a shareable `?listing=` link). Omit to keep the current URL. */
  url?: string;
  /** Media query the overlay must match to own an entry. `null` applies at every width. */
  media?: string | null;
};

/**
 * While `open` (and the media query matches), the overlay owns a history
 * entry: Back calls `onClose`, and closing it any other way pops the entry.
 */
export function useBackToClose(open: boolean, onClose: () => void, options: BackToCloseOptions = {}) {
  const { url, media = BACK_TO_CLOSE_PHONE_QUERY } = options;
  const matches = useMediaQuery(media ?? '(min-width: 0px)', { noSsr: true });
  const active = open && (media === null || matches);
  const onCloseRef = useRef(onClose);
  const urlRef = useRef(url);
  const entryRef = useRef<Entry | null>(null);

  useEffect(() => {
    onCloseRef.current = onClose;
  }, [onClose]);

  useEffect(() => {
    urlRef.current = url;
    if (entryRef.current) retarget(entryRef.current, url);
  }, [url]);

  useEffect(() => {
    if (!active) return;
    const entry = openEntry(() => onCloseRef.current(), urlRef.current);
    entryRef.current = entry;
    return () => {
      entryRef.current = null;
      release(entry);
    };
  }, [active]);
}
